import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';

/**
 * Zobrazovací (prezentační) měna (Fáze 2, #4). Ceny se přepočítají kurzem pro
 * zobrazení skrz celou výlohu VČETNĚ košíku a pokladny; settlement ale zůstává
 * v CZK (skutečná platba), proto se u cizí měny ukazuje, že jde o orientační
 * přepočet a účtuje se v korunách. Objednávka si měnu i kurz uloží.
 *
 * Kurzy se stahují z ČNB přes `/api/shop/rates`; než dorazí (nebo při výpadku)
 * se použijí statické jako fallback.
 */
export const CURRENCIES = ['CZK', 'EUR', 'PLN'] as const;
export type Currency = (typeof CURRENCIES)[number];

/** Kolik jednotek měny je 1 Kč – statický fallback (shoda se serverem). */
const RATES: Record<Currency, number> = {
  CZK: 1,
  EUR: 0.0398,
  PLN: 0.169,
};

const STORAGE_KEY = 'zooshopik.currency';

interface MoneyContextValue {
  readonly currency: Currency;
  setCurrency(currency: Currency): void;
  /** Naformátuje částku (v halířích CZK) do aktuální zobrazovací měny. */
  format(minorCzk: number): string;
  /** Aktuální kurz – kolik jednotek zvolené měny je 1 Kč (CZK => 1). */
  readonly rate: number;
  /** true, když jde o orientační přepočet (jiná měna než CZK). */
  readonly isConverted: boolean;
}

const MoneyContext = createContext<MoneyContextValue | null>(null);

function initialCurrency(): Currency {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return (CURRENCIES as readonly string[]).includes(stored ?? '') ? (stored as Currency) : 'CZK';
  } catch {
    return 'CZK';
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);
  const [rates, setRates] = useState<Record<Currency, number>>(RATES);

  // Živé kurzy z ČNB (přes server). Chyba/výpadek = zůstane fallback.
  useEffect(() => {
    let alive = true;
    void api
      .rates()
      .then((result) => {
        if (!alive) return;
        const eur = result.rates.EUR;
        const pln = result.rates.PLN;
        setRates({
          CZK: 1,
          EUR: typeof eur === 'number' && Number.isFinite(eur) && eur > 0 ? eur : RATES.EUR,
          PLN: typeof pln === 'number' && Number.isFinite(pln) && pln > 0 ? pln : RATES.PLN,
        });
      })
      .catch(() => {
        // Ticho – fallback kurzy stačí, výloha nesmí spadnout na kurzech.
      });
    return () => {
      alive = false;
    };
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Privátní režim – volba nepřežije zavření okna.
    }
  }, []);

  const rate = rates[currency] ?? 1;

  const format = useCallback(
    (minorCzk: number) => {
      const amount = (minorCzk / 100) * rate;
      const whole = Math.abs(amount % 1) < 0.005;
      return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency,
        maximumFractionDigits: whole ? 0 : 2,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(amount);
    },
    [currency, rate],
  );

  const value = useMemo(
    () => ({ currency, setCurrency, format, rate, isConverted: currency !== 'CZK' }),
    [currency, setCurrency, format, rate],
  );
  return <MoneyContext.Provider value={value}>{children}</MoneyContext.Provider>;
}

export function useMoney(): MoneyContextValue {
  const context = useContext(MoneyContext);
  if (context === null) throw new Error('useMoney musí být uvnitř <CurrencyProvider>.');
  return context;
}
